import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!phone.trim()) {
      setError('Phone number is required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { name, email, password, phone_number: phone });
      setSuccess(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Registration failed');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex" data-testid="register-success-page">
        <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24 max-w-[600px]">
          <div className="flex items-center gap-2 mb-8">
            <Zap className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={2.5} />
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>ZipAI</span>
          </div>
          <div className="w-12 h-12 rounded-full bg-[hsl(var(--success))]/20 flex items-center justify-center mb-6">
            <Check className="w-6 h-6 text-[hsl(var(--success))]" />
          </div>
          <h1 className="text-3xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Check your email
          </h1>
          <p className="text-muted-foreground mb-2">
            We've sent a verification link to <strong className="text-foreground">{email}</strong>.
          </p>
          <p className="text-muted-foreground mb-8 text-sm">
            After verifying your email, an administrator will review and approve your account. You'll receive an email once approved.
          </p>
          <Link to="/login">
            <Button variant="outline" className="rounded-[4px]" data-testid="back-to-login-btn">Back to login</Button>
          </Link>
          <p className="mt-16 text-xs text-muted-foreground/60 tracking-wide text-center">Powered By Cuvano&reg;</p>
        </div>
        <div className="hidden lg:block flex-1 relative">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1640346876473-f76a73c71539?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjB0ZXh0dXJlJTIwbWluaW1hbHxlbnwwfHx8fDE3NzQ5NTc3OTJ8MA&ixlib=rb-4.1.0&q=85')` }} />
          <div className="absolute inset-0 bg-black/40" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24 max-w-[600px]">
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-8">
            <Zap className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={2.5} />
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>ZipAI</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Create account
          </h1>
          <p className="text-muted-foreground text-base">Get started with ZipAI receipt automation</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 px-4 py-3 rounded-md" data-testid="register-error">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Full Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" required data-testid="register-name-input" className="h-11 rounded-[4px]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="register-email-input" className="h-11 rounded-[4px]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">WhatsApp Phone Number</Label>
            <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+60123456789" required data-testid="register-phone-input" className="h-11 rounded-[4px]" />
            <p className="text-xs text-muted-foreground">Include country code (e.g. +60). Used for WhatsApp receipt submissions.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required data-testid="register-password-input" className="h-11 rounded-[4px]" />
          </div>

          <Button type="submit" disabled={loading} className="w-full h-11 rounded-[4px] font-semibold active:scale-[0.98] transition-transform" data-testid="register-submit-btn">
            {loading ? 'Creating account...' : 'Create account'}
            {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>

        <p className="mt-8 text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-foreground font-medium hover:underline" data-testid="login-link">Sign in</Link>
        </p>

        <p className="mt-16 text-xs text-muted-foreground/60 tracking-wide text-center">Powered By Cuvano&reg;</p>
      </div>

      <div className="hidden lg:block flex-1 relative">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1640346876473-f76a73c71539?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjB0ZXh0dXJlJTIwbWluaW1hbHxlbnwwfHx8fDE3NzQ5NTc3OTJ8MA&ixlib=rb-4.1.0&q=85')` }} />
        <div className="absolute inset-0 bg-black/40" />
      </div>
    </div>
  );
}
